import { createContext, useContext, useState } from 'react';

// Twelve common business zones for the dropdown. Keys are IANA zones; the
// label shows a friendly abbreviation + city so users can pick by mental model.
// The list deliberately covers North America, Europe, Middle East, Asia, and
// Oceania so a distributed BIM team doesn't have to search.
export const BUSINESS_TZS = [
  { label: 'PT  — Vancouver / Los Angeles', value: 'America/Los_Angeles' },
  { label: 'MT  — Denver / Calgary',         value: 'America/Denver' },
  { label: 'CT  — Chicago / Mexico City',    value: 'America/Chicago' },
  { label: 'ET  — Toronto / New York',       value: 'America/New_York' },
  { label: 'UTC',                            value: 'UTC' },
  { label: 'CET — Madrid / Paris',           value: 'Europe/Madrid' },
  { label: 'GMT — London / Dublin',          value: 'Europe/London' },
  { label: 'GST — Dubai',                    value: 'Asia/Dubai' },
  { label: 'SGT — Singapore',                value: 'Asia/Singapore' },
  { label: 'JST — Tokyo',                    value: 'Asia/Tokyo' },
  { label: 'AEST — Sydney',                  value: 'Australia/Sydney' },
  { label: 'NZST — Auckland',                value: 'Pacific/Auckland' },
];

const browserDefault = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const TimezoneContext = createContext(null);

export const TimezoneProvider = ({ children }) => {
  const [tz, setTzState] = useState(() => {
    try {
      const stored = localStorage.getItem('aha_tz');
      if (stored) return stored;
    } catch {}
    return browserDefault();
  });

  const setTz = (newTz) => {
    setTzState(newTz);
    try { localStorage.setItem('aha_tz', newTz); } catch {}
  };

  // Format helpers — keep callers small and consistent across pages.
  // Output: "May 22, 2026, 11:27 AM PDT" (Intl picks the right abbrev).
  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZoneName: 'short',
      }).format(d);
    } catch {
      return d.toISOString();
    }
  };

  // Human-friendly duration between two ISO timestamps. Returns null when
  // either is missing or the math doesn't work. The Recent IFC Validations
  // panel renders this column.
  const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return null;
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
    const sec = Math.round((end - start) / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    const remSec = sec % 60;
    if (min < 60) return remSec ? `${min}m ${remSec}s` : `${min}m`;
    const h = Math.floor(min / 60);
    const remMin = min % 60;
    return remMin ? `${h}h ${remMin}m` : `${h}h`;
  };

  // Short abbreviation for the sidebar badge — uses the live tz, picks the
  // abbreviation Intl would render right now (handles DST automatically).
  const shortTzLabel = () => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, timeZoneName: 'short',
      }).formatToParts(new Date());
      return parts.find((p) => p.type === 'timeZoneName')?.value || tz;
    } catch {
      return tz;
    }
  };

  return (
    <TimezoneContext.Provider value={{
      tz,
      setTz,
      zones: BUSINESS_TZS,
      formatDateTime,
      formatDuration,
      shortTzLabel,
      browserDefault: browserDefault(),
    }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => {
  const ctx = useContext(TimezoneContext);
  if (!ctx) {
    // Fall back to a no-op formatter so a missing provider does not crash
    // pages during HMR. The real provider wraps App in App.js.
    return {
      tz: 'UTC',
      setTz: () => {},
      zones: BUSINESS_TZS,
      formatDateTime: (iso) => (iso ? new Date(iso).toISOString() : ''),
      formatDuration: () => null,
      shortTzLabel: () => 'UTC',
      browserDefault: 'UTC',
    };
  }
  return ctx;
};
